import { useCallback, useMemo, useState } from "react";

export const DEFAULT_CLIENT_FILTERS = {
  inconsistente: false,
  opcoes_cadastro: [],
  potencial: [],
  genero: [],
  estado_civil: [],
  aplicativos: [],
  bairro: [],
  cidade: [],
  uf: [],
  tags: [],
  id_interno: "",
  nome: "",
  email: "",
  celular: "",
  telefone: "",
  cpf_cnpj: "",
  identidade: "",
  profissao: "",
  cargo: "",
  score_min: "",
  score_max: "",
  data_tipo: "todo-periodo",
  data_inicio: "",
  data_fim: "",
  portal_acesso: "",
};

function appendArrayParams(params, key, values) {
  const list = Array.isArray(values) ? values.filter(Boolean) : [];
  if (list.length) params.set(key, list.join(","));
}

/**
 * Serializa filtros para query string do GET /clients.
 */
export function buildClientFiltersQuery(filters = {}) {
  const params = new URLSearchParams();
  const f = { ...DEFAULT_CLIENT_FILTERS, ...filters };

  if (f.inconsistente) params.set("inconsistente", "1");

  appendArrayParams(params, "opcoes_cadastro", f.opcoes_cadastro);
  appendArrayParams(params, "potencial", f.potencial);
  appendArrayParams(params, "genero", f.genero);
  appendArrayParams(params, "estado_civil", f.estado_civil);
  appendArrayParams(params, "aplicativos", f.aplicativos);
  appendArrayParams(params, "bairro", f.bairro);
  appendArrayParams(params, "cidade", f.cidade);
  appendArrayParams(params, "uf", f.uf);
  appendArrayParams(params, "tags", f.tags);

  const textFields = [
    "id_interno",
    "nome",
    "email",
    "celular",
    "telefone",
    "cpf_cnpj",
    "identidade",
    "profissao",
    "cargo",
    "data_tipo",
    "data_inicio",
    "data_fim",
  ];
  textFields.forEach((key) => {
    const v = String(f[key] ?? "").trim();
    if (v) params.set(key, key === "data_tipo" && v === "entrada" ? "cadastro" : v);
  });

  if (f.score_min !== "" && f.score_min != null) params.set("score_min", String(f.score_min));
  if (f.score_max !== "" && f.score_max != null) params.set("score_max", String(f.score_max));

  const portalAccess = String(f.portal_acesso || "").trim().toLowerCase();
  if (portalAccess === "com" || portalAccess === "sem") {
    params.set("portal_acesso", portalAccess);
  }

  return params.toString();
}

export function countActiveClientFilters(filters = {}) {
  const f = { ...DEFAULT_CLIENT_FILTERS, ...filters };
  let n = 0;
  if (f.inconsistente) n += 1;
  ["opcoes_cadastro", "potencial", "genero", "estado_civil", "aplicativos", "bairro", "cidade", "uf", "tags"].forEach(
    (k) => {
      if (Array.isArray(f[k]) && f[k].length) n += 1;
    }
  );
  ["id_interno", "nome", "email", "celular", "telefone", "cpf_cnpj", "identidade", "profissao", "cargo"].forEach(
    (k) => {
      if (String(f[k] || "").trim()) n += 1;
    }
  );
  if (f.score_min !== "" && f.score_min != null) n += 1;
  if (f.score_max !== "" && f.score_max != null) n += 1;
  if (f.data_tipo && f.data_tipo !== "todo-periodo" && (f.data_inicio || f.data_fim)) n += 1;
  if (f.portal_acesso === "com" || f.portal_acesso === "sem") n += 1;
  return n;
}

export function useClientFilters(initial = DEFAULT_CLIENT_FILTERS) {
  const [filters, setFilters] = useState({ ...DEFAULT_CLIENT_FILTERS, ...initial });
  const [appliedFilters, setAppliedFilters] = useState({ ...DEFAULT_CLIENT_FILTERS, ...initial });

  const patchFilters = useCallback((patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  const applyFilters = useCallback(() => {
    setAppliedFilters({ ...filters });
  }, [filters]);

  const clearFilters = useCallback(() => {
    setFilters({ ...DEFAULT_CLIENT_FILTERS });
    setAppliedFilters({ ...DEFAULT_CLIENT_FILTERS });
  }, []);

  const resetDraftToApplied = useCallback(() => {
    setFilters({ ...appliedFilters });
  }, [appliedFilters]);

  const draftActiveCount = useMemo(() => countActiveClientFilters(filters), [filters]);

  const queryString = useMemo(() => buildClientFiltersQuery(appliedFilters), [appliedFilters]);
  const activeCount = useMemo(() => countActiveClientFilters(appliedFilters), [appliedFilters]);
  const hasActiveFilters = activeCount > 0;

  return {
    filters,
    appliedFilters,
    patchFilters,
    applyFilters,
    clearFilters,
    resetDraftToApplied,
    draftActiveCount,
    queryString,
    activeCount,
    hasActiveFilters,
  };
}
